/**
 * Settings > Claims > Reminders (WPS-157 §13).
 *
 * System-level documentation-reminder cadence, kept separately for claims and
 * transfers, so it is an admin setting rather than something users change per
 * record. Blank = not set (the scheduled sweep falls back to the tenant
 * trigger schedule for that field).
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  useReminderSettings,
  useUpdateReminderSettings,
} from '@/admin/hooks/use-reminder-settings'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'

type FormValues = {
  claims_interval_days?: number | string
  claims_max_reminders?: number | string
  transfers_interval_days?: number | string
  transfers_max_reminders?: number | string
}

function toNullableInt(value: unknown): number | null {
  if (value === '' || value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null
}

export function ClaimRemindersTab() {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useReminderSettings()
  const update = useUpdateReminderSettings()

  const fields = useMemo<FormField[]>(() => {
    const claims = data?.claims
    const transfers = data?.transfers
    const num = (
      name: string,
      label: string,
      value: number | null | undefined,
      placeholder: string,
    ): FormField => ({
      type: 'number',
      name,
      label,
      value: value ?? '',
      placeholder,
    })
    return [
      num(
        'claims_interval_days',
        t('claimReminders.claimsInterval', 'Claims - days between reminders'),
        claims?.interval_days,
        'e.g. 7',
      ),
      num(
        'claims_max_reminders',
        t('claimReminders.claimsMax', 'Claims - maximum reminders'),
        claims?.max_reminders,
        'e.g. 3',
      ),
      num(
        'transfers_interval_days',
        t('claimReminders.transfersInterval', 'Transfers - days between reminders'),
        transfers?.interval_days,
        'e.g. 5',
      ),
      num(
        'transfers_max_reminders',
        t('claimReminders.transfersMax', 'Transfers - maximum reminders'),
        transfers?.max_reminders,
        'e.g. 4',
      ),
    ]
  }, [data, t])

  const { Form, FormFooter, ref } = useForm(
    { name: `reminder-settings-${data ? 'ready' : 'empty'}`, submit: 'footer', fields },
    async (raw) => {
      const v = raw as FormValues
      try {
        await update.mutateAsync({
          claims: {
            interval_days: toNullableInt(v.claims_interval_days),
            max_reminders: toNullableInt(v.claims_max_reminders),
          },
          transfers: {
            interval_days: toNullableInt(v.transfers_interval_days),
            max_reminders: toNullableInt(v.transfers_max_reminders),
          },
        })
        toast.success(t('claimReminders.saved', 'Reminder settings saved.'))
      } catch (err) {
        toastApiError(
          err,
          t('claimReminders.saveFailed', 'Failed to save reminder settings'),
        )
        throw err
      }
    },
    [update, data, t],
  )

  return (
    <div className="max-w-[560px] py-2">
      <h2 className="mb-1 font-heading text-[18px] text-[var(--ink)]">
        {t('claimReminders.title', 'Reminder cadence')}
      </h2>
      <p className="mb-5 text-[13px] leading-relaxed text-[var(--ink-3)]">
        {t(
          'claimReminders.description',
          'How often documentation reminders are sent, and how many, for claims and transfers awaiting customer documents. These apply account-wide - they are not set per record. Leave a field blank to use the default schedule.',
        )}
      </p>
      {isLoading ? (
        <p className="text-[13px] text-[var(--ink-3)]">
          {t('claimReminders.loading', 'Loading settings...')}
        </p>
      ) : isError ? (
        <p className="text-[13px] text-[var(--status-error)]">
          {t('claimReminders.loadError', 'Could not load reminder settings.')}
        </p>
      ) : (
        <>
          <Form className="grid grid-cols-2 gap-x-5 gap-y-5" />
          {FormFooter && (
            <div className="mt-6 flex justify-end gap-2.5 border-t-[0.5px] border-[var(--rule)] pt-5">
              <FormFooter
                onCancel={() => ref.current?.resetForm()}
                disabled={update.isPending}
                submitText={
                  update.isPending
                    ? t('claimReminders.saving', 'Saving...')
                    : t('claimReminders.save', 'Save changes')
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
